import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Reveal from '../components/Reveal';
import CategoryCard from '../components/CategoryCard';
import ProductCard from '../components/ProductCard';
import FeatureCard from '../components/FeatureCard';
import TestimonialCard from '../components/TestimonialCard';
import supabase from '../supabase/supabase';
import { assets, categories } from '../data/catalog';
import './HomeWellness.css';
import './HomeSectionsDisplay.css';

const {
  FiSun, FiAward, FiFeather, FiMapPin, FiHeart, FiMoon, FiDroplet,
  FiSmile, FiZoomIn, FiPackage, FiArrowRight
} = FiIcons;

export function FeaturedCategories() {
  const [displayCategories, setDisplayCategories] = useState(categories);

  useEffect(() => {
    const loadCategories = async () => {
      const { data, error } = await supabase
        .from('product_categories_1750000004')
        .select('*')
        .eq('active', true)
        .order('sort_order');

      if (!error && data?.length) {
        setDisplayCategories(data.map((category) => ({
          id: category.id,
          title: category.name,
          description: category.description,
          image: category.image_url || assets.ingredientCircle
        })));
      }
    };

    loadCategories();
  }, []);

  return (
    <section className="vibrant-section section-warm section-shell" id="categories">
      <Reveal className="section-heading">
        <span className="eyebrow">Explore the collection</span>
        <h2>Shop by Botanical Category</h2>
        <div className="section-divider" />
      </Reveal>
      <div className="category-grid">
        {displayCategories.map((category, index) => (
          <Reveal key={category.id || category.title} delay={index * 0.08}>
            <CategoryCard category={category} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function SignatureCollection() {
  return (
    <Reveal>
      <section className="signature section-shell">
        <img src={assets.signature} alt="Signature Sri Lankan botanical collection" />
        <div className="signature-copy">
          <span className="eyebrow">Our signature collection</span>
          <h2>Discover Sri Lanka's Finest Botanicals</h2>
          <p>Every botanical is carefully selected, naturally dried and prepared for exceptional quality.</p>
          <Link className="button primary gradient-btn" to="/shop">Browse Collection</Link>
        </div>
      </section>
    </Reveal>
  );
}

export function BestSellers({ onAdd }) {
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    const loadBestSellers = async () => {
      const { data, error } = await supabase
        .from('products_1750000001')
        .select('*')
        .eq('available', true)
        .order('id')
        .limit(4);

      if (!error && data?.length) {
        setBestSellers(data.map((product) => ({
          id: product.id,
          name: product.name,
          category: product.category,
          price: Number(product.price),
          weight: product.weight,
          stock_quantity: product.stock_quantity,
          image: product.image,
          description: product.description || '',
          slug: product.slug || '',
          available: product.available
        })));
      }
    };

    loadBestSellers();
  }, []);

  return (
    <section className="vibrant-section section-cool section-shell">
      <Reveal className="section-heading heading-row">
        <div>
          <span className="eyebrow">Customer favourites</span>
          <h2>Our Best Sellers</h2>
          <div className="section-divider" />
        </div>
        <Link to="/shop" className="text-link">View all products →</Link>
      </Reveal>
      <div className="product-grid">
        {bestSellers.map((product, index) => (
          <Reveal key={product.id} delay={index * 0.08}>
            <ProductCard product={product} onAdd={onAdd} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function OrderTrackingBanner() {
  const [trackingInput, setTrackingInput] = useState('');
  const navigate = useNavigate();

  const handleTrack = (event) => {
    event.preventDefault();
    if (trackingInput.trim()) {
      navigate('/track');
    }
  };

  return (
    <Reveal>
      <section className="tracking-banner-section section-shell">
        <div className="tracking-banner">
          <div className="tracking-banner-content">
            <div className="tracking-banner-icon">
              <SafeIcon icon={FiPackage} />
            </div>
            <div>
              <h2>Track Your Order</h2>
              <p>Enter your tracking number to see real-time order status and delivery updates</p>
            </div>
          </div>
          <form onSubmit={handleTrack} className="tracking-banner-form">
            <input
              type="text"
              value={trackingInput}
              onChange={(event) => setTrackingInput(event.target.value.toUpperCase())}
              placeholder="Enter tracking number (e.g. DG-XXXXXXXX)"
            />
            <button type="submit" className="button primary gradient-btn">
              Track <SafeIcon icon={FiArrowRight} />
            </button>
          </form>
        </div>
      </section>
    </Reveal>
  );
}

export function WhyChooseUs() {
  const features = [
    [FiSun, 'Naturally Dried', 'Preserving colour, aroma and nutrients.', 'accent-green'],
    [FiAward, 'Export Quality', 'Prepared to international standards.', 'accent-gold'],
    [FiFeather, 'Pure & Natural', 'No artificial colours or preservatives.', 'accent-rose'],
    [FiMapPin, 'Sri Lankan Heritage', 'Locally sourced premium botanicals.', 'accent-indigo']
  ];
  return (
    <section className="vibrant-section section-green section-shell" id="why">
      <Reveal className="why-visual"><img src={assets.ingredientCircle} alt="Premium botanical ingredients" /></Reveal>
      <Reveal className="why-copy">
        <span className="eyebrow">Rooted in quality</span>
        <h2>Why Choose Darshana</h2>
        <div className="section-divider" />
        <p>From careful sourcing to gentle drying, every step honours the natural character of our botanicals.</p>
        <div className="feature-grid">
          {features.map(([icon, title, description, accent]) => (
            <FeatureCard key={title} icon={icon} title={title} description={description} accent={accent} />
          ))}
        </div>
      </Reveal>
    </section>
  );
}

export function BotanicalWellness() {
  const benefits = [
    [FiHeart, 'Hibiscus', 'Bright, refreshing and antioxidant-rich.', 'accent-rose'],
    [FiMoon, 'Blue Lotus', 'A calming addition to evening rituals.', 'accent-purple'],
    [FiDroplet, 'Butterfly Pea', 'Naturally vivid and beautifully balanced.', 'accent-indigo'],
    [FiSmile, 'Rose', 'Delicate floral notes for mindful moments.', 'accent-pink']
  ];

  return (
    <section className="vibrant-section section-purple section-shell">
      <Reveal className="section-heading">
        <span className="eyebrow">Daily rituals</span>
        <h2>The Art of Botanical Wellness</h2>
        <div className="section-divider" />
      </Reveal>
      <div className="benefit-grid">
        {benefits.map(([icon, title, text, accent]) => (
          <div className={`benefit-card ${accent}`} key={title}>
            <SafeIcon icon={icon} />
            <h3>{title}</h3>
            <p>{text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Testimonials() {
  const reviews = [
    ['Amelia R.', 'AR', 'The colour and fragrance are exceptional. Every parcel feels beautifully considered.', 'Wellness studio owner'],
    ['Nimali S.', 'NS', 'Truly premium botanicals with a freshness that immediately sets them apart.', 'Verified customer'],
    ['Daniel K.', 'DK', 'Reliable export quality and thoughtful service. Darshana is now a trusted supplier.', 'Specialty retailer']
  ];
  return (
    <section className="vibrant-section section-gradient section-shell testimonials">
      <Reveal className="section-heading">
        <span className="eyebrow">Kind words</span>
        <h2>Loved by Our Community</h2>
        <div className="section-divider" />
      </Reveal>
      <div className="testimonial-grid">
        {reviews.map(([name, initials, review, role], index) => (
          <TestimonialCard key={name} name={name} initials={initials} review={review} role={role} index={index} />
        ))}
      </div>
    </section>
  );
}

export function BotanicalGallery() {
  const images = [assets.hibiscusCluster, assets.wellness, assets.lotusBloom, assets.roseSwirl, assets.peaWave, assets.hero];
  return (
    <section className="standard-section section-shell">
      <Reveal className="section-heading">
        <span className="eyebrow">From our journal</span>
        <h2>Botanical Moments</h2>
        <div className="section-divider" />
      </Reveal>
      <div className="gallery-grid">
        {images.map((image, index) => (
          <div className={`gallery-item gallery-${index + 1}`} key={image}>
            <img src={image} alt={`Botanical moment ${index + 1}`} loading="lazy" />
            <span><SafeIcon icon={FiZoomIn} /></span>
          </div>
        ))}
      </div>
    </section>
  );
}